import React, { useState } from 'react';

const SECTIONS = [
  { id: 'goals',    label: 'Project Goals (3 S.M.A.R.T.)', pct: 100, note: 'All 3 goals set · Year-end reflection pending',          status: 'done' },
  { id: 'weight',   label: 'Weight & Growth Log',           pct: 85,  note: '17 of 20 required weigh-ins recorded',                   status: 'done' },
  { id: 'financial',label: 'Financial Records',              pct: 72,  note: '$384 expenses · $0 income · Sale price needed',          status: 'progress' },
  { id: 'health',   label: 'Health & Treatment Records',    pct: 60,  note: 'Producer Affidavit (C1051E) not yet completed',          status: 'progress' },
  { id: 'narrative',label: 'Reflective Narrative',           pct: 20,  note: 'Draft started · 3 guided prompts unanswered',            status: 'needs' },
  { id: 'activity', label: 'Activity Journal',               pct: 80,  note: 'Meetings, shows, and community service logged',          status: 'done' },
  { id: 'photos',   label: 'Photo Pages',                    pct: 80,  note: '8 of 10 suggested photos · Auto-curated from log',       status: 'done' },
];

const OVERALL = Math.round(SECTIONS.reduce((a, s) => a + s.pct, 0) / SECTIONS.length);

const STATUS_MAP = {
  done:     { label: 'Complete',    bg: '#E8F5EE', color: 'var(--forest-mid)',  barColor: 'var(--forest-mid)' },
  progress: { label: 'In progress', bg: '#FEF3E2', color: 'var(--warning)',    barColor: 'var(--warning)' },
  needs:    { label: 'Needs attention', bg: '#FEE8E8', color: 'var(--danger)', barColor: 'var(--danger)' },
};

const PROMPTS = {
  narrative: [
    'What was the hardest part of your project this year?',
    'What would you do differently next time?',
    'How did you solve a problem that came up?',
  ],
};

export default function RecordBook({ onNavigate }) {
  const [expanded, setExpanded] = useState(null);
  const [narrativeIdx, setNarrativeIdx] = useState(0);
  const [exportPressed, setExportPressed] = useState(false);
  const canExport = OVERALL >= 90;

  const handleExport = () => {
    if (!canExport) return;
    setExportPressed(true);
    setTimeout(() => setExportPressed(false), 2500);
  };

  return (
    <div style={{overflowY:'auto', height:'100%', paddingBottom:24}}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.h2}>Record Book</div>
        <div style={s.hsub}>Emma Dawson · 2025–2026 · Dairy Goat — Clover</div>
        <div style={s.template}>WA State C0934 · Due June 15</div>
      </div>

      {/* Overall ring */}
      <div style={s.ringWrap}>
        <OverallRing pct={OVERALL} />
        <div style={s.ringLabel}>{OVERALL}% complete</div>
        <div style={s.ringNote}>
          {SECTIONS.filter(s => s.status !== 'done').length} section{SECTIONS.filter(s => s.status !== 'done').length !== 1 ? 's' : ''} need attention before export
        </div>
      </div>

      {/* Sections */}
      <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
        {SECTIONS.map(sec => {
          const st = STATUS_MAP[sec.status];
          const isOpen = expanded === sec.id;
          return (
            <div key={sec.id} style={s.sectionCard(isOpen)} onClick={() => setExpanded(isOpen ? null : sec.id)}>
              <div style={s.scHeader}>
                <div style={s.scTitle}>{sec.label}</div>
                <div style={s.scRight}>
                  <span style={{...s.badge, background: st.bg, color: st.color}}>{st.label}</span>
                  <span style={s.chevron(isOpen)}>›</span>
                </div>
              </div>
              <div style={s.barBg}>
                <div style={{...s.barFill, width:`${sec.pct}%`, background: st.barColor}} />
              </div>
              <div style={s.scNote}>{sec.note}</div>

              {/* Expanded panel */}
              {isOpen && sec.id === 'narrative' && (
                <div style={s.expandPanel} className="fade-in" onClick={e => e.stopPropagation()}>
                  <div style={s.promptLabel}>Guided prompt {narrativeIdx + 1} of {PROMPTS.narrative.length}</div>
                  <div style={s.promptQ}>{PROMPTS.narrative[narrativeIdx]}</div>
                  <textarea style={s.promptInput} placeholder="Write your response here…" rows={4} />
                  <div style={{display:'flex', gap:8, marginTop:10}}>
                    <button style={s.promptBtn('secondary')} onClick={() => setNarrativeIdx(i => Math.max(0, i-1))} disabled={narrativeIdx === 0}>← Prev</button>
                    <button style={s.promptBtn('primary')} onClick={() => setNarrativeIdx(i => Math.min(PROMPTS.narrative.length-1, i+1))}>
                      {narrativeIdx < PROMPTS.narrative.length - 1 ? 'Next prompt →' : 'Save narrative'}
                    </button>
                  </div>
                </div>
              )}
              {isOpen && sec.id === 'health' && (
                <div style={s.expandPanel} className="fade-in" onClick={e => e.stopPropagation()}>
                  <div style={s.infoRow}>
                    <div style={s.infoLabel}>Producer Affidavit</div>
                    <span style={{...s.badge, background:'#FEF3E2', color:'var(--warning)'}}>Incomplete</span>
                  </div>
                  <div style={{fontSize:12, color:'var(--text-muted)', marginTop:6}}>
                    Required for market animals. Complete C1051E (Goat/Sheep) before submission.
                  </div>
                  <button style={{...s.promptBtn('primary'), marginTop:10, width:'100%'}}>Open Producer Affidavit →</button>
                </div>
              )}
              {isOpen && sec.id === 'financial' && (
                <div style={s.expandPanel} className="fade-in" onClick={e => e.stopPropagation()}>
                  <FinancialMini />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Export */}
      <div style={{padding:'16px 16px 0'}}>
        <button style={s.exportBtn(canExport)} onClick={handleExport}>
          {exportPressed ? '✓ Generating PDF…' : canExport ? 'Export WA State Record Book PDF' : `Complete ${100 - OVERALL}% more to export`}
        </button>
        {!canExport && (
          <div style={{fontSize:11, color:'var(--text-muted)', textAlign:'center', marginTop:8, lineHeight:1.4}}>
            Record book must be 100% complete before official export.<br/>
            3 sections still need your attention.
          </div>
        )}
      </div>
    </div>
  );
}

function OverallRing({ pct }) {
  const r = 52, circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div style={{position:'relative', width:120, height:120}}>
      <svg width="120" height="120" style={{transform:'rotate(-90deg)'}}>
        <circle cx="60" cy="60" r={r} fill="none" stroke="#E8F0EC" strokeWidth="10"/>
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--forest-mid)" strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"/>
      </svg>
      <div style={{position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--ff-display)', fontSize:26, color:'var(--text-head)'}}>
        {pct}%
      </div>
    </div>
  );
}

function FinancialMini() {
  const items = [
    { label: 'Animal purchase', type: 'exp', val: '$175.00' },
    { label: 'Feed (14 wks)',   type: 'exp', val: '$124.50' },
    { label: 'Vet / health',    type: 'exp', val: '$62.00' },
    { label: 'Entry fees',      type: 'exp', val: '$22.50' },
    { label: 'Sale price',      type: 'inc', val: '— needed' },
  ];
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom: i < items.length-1 ? '1px solid var(--border-light)' : 'none'}}>
          <span style={{fontSize:12, color:'var(--text-body)'}}>{item.label}</span>
          <span style={{fontSize:12, fontFamily:'var(--ff-mono)', fontWeight:500, color: item.type==='exp' ? 'var(--burg)' : item.val.includes('needed') ? 'var(--warning)' : 'var(--positive)'}}>
            {item.val}
          </span>
        </div>
      ))}
      <div style={{display:'flex', justifyContent:'space-between', marginTop:8, paddingTop:8, borderTop:'2px solid var(--border)'}}>
        <span style={{fontSize:12, fontWeight:600, color:'var(--text-head)'}}>Total Expenses</span>
        <span style={{fontSize:12, fontFamily:'var(--ff-mono)', fontWeight:600, color:'var(--burg)'}}>$384.00</span>
      </div>
    </div>
  );
}

const s = {
  header: {
    background:'var(--forest-primary)', padding:'16px 16px 18px',
  },
  h2: { fontFamily:'var(--ff-display)', fontSize:22, color:'#fff', marginBottom:4 },
  hsub: { fontSize:12, color:'var(--forest-pale)' },
  template: {
    display:'inline-block', marginTop:8,
    background:'rgba(255,255,255,0.12)', borderRadius:20,
    padding:'3px 10px', fontSize:11, color:'#fff',
  },
  ringWrap: {
    padding:'20px 16px 16px',
    display:'flex', flexDirection:'column', alignItems:'center', gap:6,
    background:'var(--surface)',
    borderBottom:'1px solid var(--border)',
  },
  ringLabel: { fontFamily:'var(--ff-display)', fontSize:18, color:'var(--text-head)' },
  ringNote: { fontSize:12, color:'var(--text-muted)', textAlign:'center' },
  sectionCard: (open) => ({
    background:'var(--surface)',
    border:`1px solid ${open ? 'var(--forest-mid)' : 'var(--border)'}`,
    borderRadius:12,
    padding:'12px 14px',
    cursor:'pointer',
    transition:'border-color 0.15s',
  }),
  scHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8, gap:8 },
  scTitle: { fontSize:13, fontWeight:600, color:'var(--text-head)', flex:1 },
  scRight: { display:'flex', alignItems:'center', gap:6, flexShrink:0 },
  badge: { fontSize:10, padding:'3px 8px', borderRadius:20, fontWeight:600 },
  chevron: (open) => ({
    fontSize:18, color:'var(--text-muted)',
    transform: open ? 'rotate(90deg)' : 'none',
    transition:'transform 0.15s',
    lineHeight:1,
  }),
  barBg: { height:4, background:'#E8F0EC', borderRadius:2 },
  barFill: { height:'100%', borderRadius:2, transition:'width 0.4s' },
  scNote: { fontSize:11, color:'var(--text-muted)', marginTop:6 },
  expandPanel: {
    marginTop:12,
    paddingTop:12,
    borderTop:'1px solid var(--border-light)',
  },
  promptLabel: { fontSize:10, fontWeight:600, color:'var(--text-muted)', letterSpacing:'0.07em', textTransform:'uppercase', marginBottom:6 },
  promptQ: { fontSize:14, color:'var(--text-head)', fontFamily:'var(--ff-display)', lineHeight:1.4, marginBottom:10 },
  promptInput: {
    width:'100%', background:'#F8FAF9', border:'1px solid var(--border)',
    borderRadius:8, padding:'8px 10px', fontSize:13, color:'var(--text-head)',
    outline:'none', resize:'none', fontFamily:'var(--ff-sans)',
  },
  promptBtn: (type) => ({
    flex: type === 'primary' ? 2 : 1,
    background: type === 'primary' ? 'var(--forest-mid)' : 'var(--surface)',
    color: type === 'primary' ? '#fff' : 'var(--text-body)',
    border: type === 'primary' ? 'none' : '1px solid var(--border)',
    borderRadius:8, padding:'9px 12px',
    fontSize:12, fontWeight:500, cursor:'pointer',
    fontFamily:'var(--ff-sans)',
  }),
  infoRow: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  infoLabel: { fontSize:13, fontWeight:600, color:'var(--text-head)' },
  exportBtn: (canExport) => ({
    width:'100%',
    background: canExport ? 'var(--burg)' : '#C8C0BC',
    color:'#fff', border:'none',
    borderRadius:12, padding:14,
    fontSize:14, fontWeight:600,
    cursor: canExport ? 'pointer' : 'default',
    fontFamily:'var(--ff-sans)',
    transition:'background 0.2s',
  }),
};
